import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import { Validator } from "Utils/validator";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  VIEW_PROFILE_SECTIONS,
} from "Constant/constants";

export const ExperienceModal = ({
  show,
  experienceData,
  toggle,
  fetchData,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    gid: "",
    title: "",
    company: "",
    employmentType: "",
    location: "",
    startDate: "",
    endDate: "",
    responsibility: "",
    currentlyWorking: false,
  });
  const [errMsg, setErrMsg] = useState({
    title: "",
    company: "",
    employmentType: "",
    startDate: "",
    endDate: "",
  });

  const {
    gid,
    title,
    company,
    employmentType,
    location,
    startDate,
    endDate,
    responsibility,
    currentlyWorking,
  } = formData;

  const validationRequiredFields = [
    "title",
    "company",
    "employmentType",
    "startDate",
  ];

  useEffect(() => {
    if (experienceData) {
      setFormData({
        gid: experienceData.gid || "",
        title: experienceData.title || "",
        company: experienceData.company || "",
        employmentType: experienceData.employmentType || "",
        location: experienceData.location || "",
        startDate: experienceData.startDate || "",
        endDate: experienceData.endDate || "",
        responsibility: experienceData.responsibility || "",
        currentlyWorking: experienceData.currentlyWorking || false,
      });
    }
  }, [experienceData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationRequiredFields.includes(name)) {
      if (!Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(
            `common.messages.error.viewProfile.experience.${name}.blank`
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handleCurrentlyWorkingChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      currentlyWorking: checked,
      endDate: checked ? "" : prev.endDate,
    }));
    if (checked) {
      setErrMsg((prev) => ({
        ...prev,
        endDate: "",
      }));
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      // Validate always-required fields
      for (const field of validationRequiredFields) {
        const value = formData[field];
        if (!Validator.text(value)) {
          errors[field] = t(
            `common.messages.error.viewProfile.experience.${field}.blank`
          );
          isValid = false;
        }
      }

      // Validate endDate only if currentlyWorking is false
      if (!formData.currentlyWorking && !Validator.text(formData.endDate)) {
        errors.endDate = t(
          "common.messages.error.viewProfile.experience.endDate.blank"
        );
        isValid = false;
      }

      // Set error messages
      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSaveExperience = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.experience,
          [VIEW_PROFILE_SECTIONS.experience]: {
            title,
            company,
            employmentType,
            location,
            startDate,
            endDate,
            responsibility,
            currentlyWorking,
          },
        };

        if (gid.trim()) {
          payload[VIEW_PROFILE_SECTIONS.experience].gid = gid;
        }

        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t("auth.viewProfile.experienceAndHighlights")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="title" className="form-label">
                {t("common.label.title")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="title"
                name="title"
                placeholder={t("common.placeHolder.titleExperienceHighlights")}
                value={title}
                onChange={handleChange}
              />
              {errMsg.title && <p className="error">{errMsg.title}</p>}
            </div>
          </div>

          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="company" className="form-label">
                {t("common.label.companyOrOrganization")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="company"
                name="company"
                placeholder={t("common.placeHolder.companyOrOrganization")}
                value={company}
                onChange={handleChange}
              />
              {errMsg.company && <p className="error">{errMsg.company}</p>}
            </div>
          </div>

          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="employmentType" className="form-label">
                {t("common.label.employmentType")}{" "}
                <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="employmentType"
                name="employmentType"
                aria-label="Default select example"
                value={employmentType}
                onChange={handleChange}
              >
                <option value="">
                  {t("common.placeHolder.employmentType")}
                </option>
                {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
              {errMsg.employmentType && (
                <p className="error">{errMsg.employmentType}</p>
              )}
            </div>
          </div>
          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="location" className="form-label">
                {t("common.label.location")}
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  id="location"
                  name="location"
                  placeholder={t("common.placeHolder.location")}
                  value={location}
                  onChange={handleChange}
                />
                <span className="input-group-text" id="basic-addon2">
                  <i className="bx bx-map"></i>
                </span>
              </div>
            </div>
          </div>
          <div className="col-md-12 col-sm-12 col-12">
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="currentlyWorking"
                checked={currentlyWorking}
                onChange={handleCurrentlyWorkingChange}
              />
              <label className="form-check-label" htmlFor="currentlyWorking">
                {t("common.label.currentlyWorkingHere")}
              </label>
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="startDate" className="form-label">
                {t("common.label.startDate")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                id="startDate"
                name="startDate"
                placeholder={t("common.placeHolder.startDate")}
                value={startDate}
                onChange={handleChange}
              />
              {errMsg.startDate && <p className="error">{errMsg.startDate}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="endDate" className="form-label">
                {t("common.label.endDate")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                id="endDate"
                name="endDate"
                placeholder={t("common.placeHolder.endDate")}
                value={endDate}
                onChange={handleChange}
                disabled={currentlyWorking}
              />
              {errMsg.endDate && <p className="error">{errMsg.endDate}</p>}
            </div>
          </div>
          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="responsibility" className="form-label">
                {t("common.label.responsibility")}
              </label>
              <textarea
                className="form-control"
                id="responsibility"
                name="responsibility"
                placeholder={t("common.placeHolder.responsibility")}
                rows="3"
                value={responsibility}
                onChange={handleChange}
              ></textarea>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button variant="primary" onClick={onSaveExperience}>
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
